import { useState } from "react";
import { Badge } from "@/components/Badge";

interface TagSelectorProps {
  selectedTags: string[];
  availableTags: string[];
  onChange: (tags: string[]) => void;
  compact?: boolean;
}

export function TagSelector({ selectedTags, availableTags, onChange, compact = false }: TagSelectorProps) {
  const [selectedOption, setSelectedOption] = useState("");
  const [newTag, setNewTag] = useState("");
  const uniqueAvailable = Array.from(new Set(availableTags.filter(Boolean))).sort();
  const selectableTags = uniqueAvailable.filter(tag => !selectedTags.includes(tag));
  const isNewTag = selectedOption === "__new__";

  function addTag() {
    const tag = (isNewTag ? newTag : selectedOption).trim();
    if (!tag || selectedTags.includes(tag)) return;
    onChange([...selectedTags, tag]);
    setSelectedOption("");
    setNewTag("");
  }

  function removeTag(tag: string) {
    onChange(selectedTags.filter(t => t !== tag));
  }

  return (
    <div className="flex flex-col gap-1.5" data-testid="tag-selector">
      <div className="flex flex-wrap gap-1 min-h-5">
        {selectedTags.length > 0 ? selectedTags.map(tag => (
          <span key={tag} className="inline-flex items-center gap-1">
            <Badge variant="muted">{tag}</Badge>
            <button
              type="button"
              className="text-red text-xs bg-transparent border-none cursor-pointer px-0.5"
              onClick={() => removeTag(tag)}
              aria-label={`Remove tag ${tag}`}
            >
              x
            </button>
          </span>
        )) : (
          <span className="text-xs text-text2">No tags selected</span>
        )}
      </div>
      <div className="flex items-center gap-1">
        <select
          className={`bg-bg border border-border rounded-default px-2 py-1 text-text text-xs ${compact ? "flex-1" : "min-w-40"}`}
          value={selectedOption}
          onChange={e => setSelectedOption(e.target.value)}
          aria-label="Requirement tag"
        >
          <option value="">Select tag...</option>
          {selectableTags.map(tag => <option key={tag} value={tag}>{tag}</option>)}
          <option value="__new__">New tag...</option>
        </select>
        {isNewTag && (
          <input
            type="text"
            value={newTag}
            onChange={e => setNewTag(e.target.value)}
            placeholder="New tag"
            className="min-w-0 flex-1 bg-bg border border-border rounded-default px-2 py-1 text-text text-xs outline-none focus:border-accent placeholder:text-text2"
            aria-label="New requirement tag"
          />
        )}
        <button
          type="button"
          className="text-accent text-xs bg-transparent border border-accent rounded-default px-2 py-1 cursor-pointer hover:bg-accent hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isNewTag ? !newTag.trim() : !selectedOption}
          onClick={addTag}
        >
          Add Tag
        </button>
      </div>
    </div>
  );
}
